/**
 * Renewal Shared Component Constants
 *
 * 이 파일은 shared 컴포넌트에서 사용할 컴포넌트 관련 상수들을 정의합니다.
 * MUI 컴포넌트와 Figma 디자인 시스템을 기반으로 구성되었습니다.
 */

// Button Variants
export const BUTTON_VARIANTS = {
  contained: "contained",
  outlined: "outlined",
  text: "text",
} as const;

// Button Sizes
export const BUTTON_SIZES = {
  small: "small",
  medium: "medium",
  large: "large",
} as const;

// Button Colors
export const BUTTON_COLORS = {
  primary: "primary",
  secondary: "secondary",
  error: "error",
  warning: "warning",
  info: "info",
  success: "success",
  inherit: "inherit",
} as const;

// Input Variants
export const INPUT_VARIANTS = {
  standard: "standard",
  outlined: "outlined",
  filled: "filled",
} as const;

// Input Sizes
export const INPUT_SIZES = {
  small: "small",
  medium: "medium",
} as const;

// Alert Severities
export const ALERT_SEVERITIES = {
  error: "error",
  warning: "warning",
  info: "info",
  success: "success",
} as const;

// Chip Variants
export const CHIP_VARIANTS = {
  filled: "filled",
  outlined: "outlined",
} as const;

// Chip Colors
export const CHIP_COLORS = {
  default: "default",
  primary: "primary",
  secondary: "secondary",
  error: "error",
  info: "info",
  success: "success",
  warning: "warning",
} as const;

// Card Variants
export const CARD_VARIANTS = {
  elevation: "elevation",
  outlined: "outlined",
} as const;

// Dialog Max Widths
export const DIALOG_MAX_WIDTHS = {
  xs: "xs",
  sm: "sm",
  md: "md",
  lg: "lg",
  xl: "xl",
  false: false,
} as const;

// Snackbar Origins
export const SNACKBAR_ORIGINS = {
  topLeft: { vertical: "top" as const, horizontal: "left" as const },
  topCenter: { vertical: "top" as const, horizontal: "center" as const },
  topRight: { vertical: "top" as const, horizontal: "right" as const },
  bottomLeft: { vertical: "bottom" as const, horizontal: "left" as const },
  bottomCenter: { vertical: "bottom" as const, horizontal: "center" as const },
  bottomRight: { vertical: "bottom" as const, horizontal: "right" as const },
} as const;

// Tooltip Placements
export const TOOLTIP_PLACEMENTS = {
  bottomEnd: "bottom-end",
  bottomStart: "bottom-start",
  bottom: "bottom",
  leftEnd: "left-end",
  leftStart: "left-start",
  left: "left",
  rightEnd: "right-end",
  rightStart: "right-start",
  right: "right",
  topEnd: "top-end",
  topStart: "top-start",
  top: "top",
} as const;

// Table Sizes
export const TABLE_SIZES = {
  small: "small",
  medium: "medium",
} as const;

// Table Padding
export const TABLE_PADDING = {
  checkbox: "checkbox",
  none: "none",
  normal: "normal",
} as const;

// Pagination Shapes
export const PAGINATION_SHAPES = {
  rounded: "rounded",
  circular: "circular",
} as const;

// Pagination Variants
export const PAGINATION_VARIANTS = {
  text: "text",
  outlined: "outlined",
} as const;

// Pagination Colors
export const PAGINATION_COLORS = {
  standard: "standard",
  primary: "primary",
  secondary: "secondary",
} as const;

// Stepper Orientations
export const STEPPER_ORIENTATIONS = {
  horizontal: "horizontal",
  vertical: "vertical",
} as const;

// Tabs Variants
export const TABS_VARIANTS = {
  standard: "standard",
  scrollable: "scrollable",
  fullWidth: "fullWidth",
} as const;

// Tabs Scroll Buttons
export const TABS_SCROLL_BUTTONS = {
  auto: "auto",
  true: true,
  false: false,
} as const;

// Accordion Variants
export const ACCORDION_VARIANTS = {
  elevation: "elevation",
  outlined: "outlined",
} as const;

// Avatar Variants
export const AVATAR_VARIANTS = {
  circular: "circular",
  rounded: "rounded",
  square: "square",
} as const;

// Badge Variants
export const BADGE_VARIANTS = {
  standard: "standard",
  dot: "dot",
} as const;

// Badge Colors
export const BADGE_COLORS = {
  default: "default",
  primary: "primary",
  secondary: "secondary",
  error: "error",
  info: "info",
  success: "success",
  warning: "warning",
} as const;

// Progress Variants
export const PROGRESS_VARIANTS = {
  determinate: "determinate",
  indeterminate: "indeterminate",
  buffer: "buffer",
  query: "query",
} as const;

// Progress Colors
export const PROGRESS_COLORS = {
  primary: "primary",
  secondary: "secondary",
  error: "error",
  info: "info",
  success: "success",
  warning: "warning",
  inherit: "inherit",
} as const;

// Switch Colors
export const SWITCH_COLORS = {
  primary: "primary",
  secondary: "secondary",
  error: "error",
  info: "info",
  success: "success",
  warning: "warning",
  default: "default",
} as const;

// Checkbox Colors
export const CHECKBOX_COLORS = {
  primary: "primary",
  secondary: "secondary",
  error: "error",
  info: "info",
  success: "success",
  warning: "warning",
  default: "default",
} as const;

// Radio Colors
export const RADIO_COLORS = {
  primary: "primary",
  secondary: "secondary",
  error: "error",
  info: "info",
  success: "success",
  warning: "warning",
  default: "default",
} as const;

// Slider Colors
export const SLIDER_COLORS = {
  primary: "primary",
  secondary: "secondary",
} as const;

// Slider Orientations
export const SLIDER_ORIENTATIONS = {
  horizontal: "horizontal",
  vertical: "vertical",
} as const;

// Rating Sizes
export const RATING_SIZES = {
  small: "small",
  medium: "medium",
  large: "large",
} as const;

// Rating Precisions
export const RATING_PRECISIONS = {
  half: "half",
  integer: "integer",
} as const;

// Component Intent (의도/목적)
export const COMPONENT_INTENTS = {
  primary: "primary",
  secondary: "secondary",
  success: "success",
  warning: "warning",
  error: "error",
  info: "info",
  neutral: "neutral",
} as const;

// Component Emphasis (강조도)
export const COMPONENT_EMPHASIS = {
  high: "high",
  medium: "medium",
  low: "low",
  minimal: "minimal",
} as const;

// Component Sizes (일반적인 크기)
export const COMPONENT_SIZES = {
  xs: "xs",
  sm: "sm",
  md: "md",
  lg: "lg",
  xl: "xl",
} as const;

// 통합 Component 상수 객체
export const COMPONENT_CONSTANTS = {
  button: {
    variants: BUTTON_VARIANTS,
    sizes: BUTTON_SIZES,
    colors: BUTTON_COLORS,
  },
  input: {
    variants: INPUT_VARIANTS,
    sizes: INPUT_SIZES,
  },
  alert: {
    severities: ALERT_SEVERITIES,
  },
  chip: {
    variants: CHIP_VARIANTS,
    colors: CHIP_COLORS,
  },
  card: {
    variants: CARD_VARIANTS,
  },
  dialog: {
    maxWidths: DIALOG_MAX_WIDTHS,
  },
  snackbar: {
    origins: SNACKBAR_ORIGINS,
  },
  tooltip: {
    placements: TOOLTIP_PLACEMENTS,
  },
  table: {
    sizes: TABLE_SIZES,
    padding: TABLE_PADDING,
  },
  pagination: {
    shapes: PAGINATION_SHAPES,
    variants: PAGINATION_VARIANTS,
    colors: PAGINATION_COLORS,
  },
  stepper: {
    orientations: STEPPER_ORIENTATIONS,
  },
  tabs: {
    variants: TABS_VARIANTS,
    scrollButtons: TABS_SCROLL_BUTTONS,
  },
  accordion: {
    variants: ACCORDION_VARIANTS,
  },
  avatar: {
    variants: AVATAR_VARIANTS,
  },
  badge: {
    variants: BADGE_VARIANTS,
    colors: BADGE_COLORS,
  },
  progress: {
    variants: PROGRESS_VARIANTS,
    colors: PROGRESS_COLORS,
  },
  switch: {
    colors: SWITCH_COLORS,
  },
  checkbox: {
    colors: CHECKBOX_COLORS,
  },
  radio: {
    colors: RADIO_COLORS,
  },
  slider: {
    colors: SLIDER_COLORS,
    orientations: SLIDER_ORIENTATIONS,
  },
  rating: {
    sizes: RATING_SIZES,
    precisions: RATING_PRECISIONS,
  },
  // 공통 속성
  intent: COMPONENT_INTENTS,
  emphasis: COMPONENT_EMPHASIS,
  size: COMPONENT_SIZES,
} as const;

// 타입 정의
export type ButtonVariant = keyof typeof BUTTON_VARIANTS;
export type ButtonSize = keyof typeof BUTTON_SIZES;
export type ButtonColor = keyof typeof BUTTON_COLORS;
export type InputVariant = keyof typeof INPUT_VARIANTS;
export type InputSize = keyof typeof INPUT_SIZES;
export type AlertSeverity = keyof typeof ALERT_SEVERITIES;
export type ChipVariant = keyof typeof CHIP_VARIANTS;
export type ChipColor = keyof typeof CHIP_COLORS;
export type CardVariant = keyof typeof CARD_VARIANTS;
export type DialogMaxWidth = keyof typeof DIALOG_MAX_WIDTHS;
export type SnackbarOrigin = keyof typeof SNACKBAR_ORIGINS;
export type TooltipPlacement = keyof typeof TOOLTIP_PLACEMENTS;
export type TableSize = keyof typeof TABLE_SIZES;
export type TablePadding = keyof typeof TABLE_PADDING;
export type PaginationShape = keyof typeof PAGINATION_SHAPES;
export type PaginationVariant = keyof typeof PAGINATION_VARIANTS;
export type PaginationColor = keyof typeof PAGINATION_COLORS;
export type StepperOrientation = keyof typeof STEPPER_ORIENTATIONS;
export type TabsVariant = keyof typeof TABS_VARIANTS;
export type TabsScrollButton = keyof typeof TABS_SCROLL_BUTTONS;
export type AccordionVariant = keyof typeof ACCORDION_VARIANTS;
export type AvatarVariant = keyof typeof AVATAR_VARIANTS;
export type BadgeVariant = keyof typeof BADGE_VARIANTS;
export type BadgeColor = keyof typeof BADGE_COLORS;
export type ProgressVariant = keyof typeof PROGRESS_VARIANTS;
export type ProgressColor = keyof typeof PROGRESS_COLORS;
export type SwitchColor = keyof typeof SWITCH_COLORS;
export type CheckboxColor = keyof typeof CHECKBOX_COLORS;
export type RadioColor = keyof typeof RADIO_COLORS;
export type SliderColor = keyof typeof SLIDER_COLORS;
export type SliderOrientation = keyof typeof SLIDER_ORIENTATIONS;
export type RatingSize = keyof typeof RATING_SIZES;
export type RatingPrecision = keyof typeof RATING_PRECISIONS;
export type ComponentIntent = keyof typeof COMPONENT_INTENTS;
export type ComponentEmphasis = keyof typeof COMPONENT_EMPHASIS;
export type ComponentSize = keyof typeof COMPONENT_SIZES;
